import type { RouteObject } from 'react-router';

import { lazy, Suspense } from 'react';
import { Outlet, Navigate } from 'react-router';

import { Role } from 'src/enums/auth/role.enum';
import { DashboardLayout } from 'src/layouts/dashboard';

import { LoadingScreen } from 'src/components/loading-screen';

import { AdminUserDetailLayout } from 'src/sections/admin/user/admin-user-detail-layout';

import { AuthGuard, RoleBasedGuard, PermissionBasedGuard } from 'src/auth/guard';

import { usePathname } from '../hooks';

const AdminUserManagement = lazy(() => import('src/pages/admin/user/management'));
const AdminUserGeneralPage = lazy(() => import('src/pages/admin/user/general'));
const AdminUserGroupsPage = lazy(() => import('src/pages/admin/user/groups'));
const AdminUserAdminRolesPage = lazy(() => import('src/pages/admin/user/admin-roles'));
const AdminUserLicensePage = lazy(() => import('src/pages/admin/user/license'));
const AdminGroupManagement = lazy(() => import('src/pages/admin/group/management'));

function SuspenseOutlet() {
  const pathname = usePathname();
  return (
    <Suspense key={pathname} fallback={<LoadingScreen />}>
      <Outlet />
    </Suspense>
  );
}

const dashboardLayout = () => (
  <DashboardLayout>
    <SuspenseOutlet />
  </DashboardLayout>
);

export const adminRoutes: RouteObject[] = [
  {
    path: 'admin',
    element: (
      <AuthGuard>
        <RoleBasedGuard allowedRoles={[Role.Admin]}>{dashboardLayout()}</RoleBasedGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: 'user-management',
        element: (
          <PermissionBasedGuard requiredPermission="canAccessUserManagement">
            <AdminUserManagement />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'group-management',
        element: (
          <PermissionBasedGuard requiredPermission="canAccessGroupManagement">
            <AdminGroupManagement />
          </PermissionBasedGuard>
        ),
      },
      {
        path: 'user-management/:id',
        element: (
          <PermissionBasedGuard requiredPermission="canAccessUserManagement">
            <AdminUserDetailLayout />
          </PermissionBasedGuard>
        ),
        children: [
          { index: true, element: <Navigate to="general" replace /> },
          { path: 'general', element: <AdminUserGeneralPage /> },
          { path: 'groups', element: <AdminUserGroupsPage /> },
          { path: 'license', element: <AdminUserLicensePage /> },
          {
            path: 'roles',
            element: (
              <PermissionBasedGuard requiredPermission="canManageAdminRoles">
                <AdminUserAdminRolesPage />
              </PermissionBasedGuard>
            ),
          },
        ],
      },
    ],
  },
];
